//BannersManager.js

import React, { useState, useEffect } from 'react';
import api from '../../../services/api';
import toast from 'react-hot-toast';
import { Plus, Trash2, Edit2, Image as ImageIcon, ChevronLeft, ChevronRight, X, Upload, Link, Tag, Package, Lock } from 'lucide-react';

export default function BannersManager({ estabelecimentoId, corPrimaria = '#ea1d2c' }) {
    const [banners, setBanners] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [produtos, setProdutos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    
    // Estado para controle de limite do plano
    const [limiteProdutos, setLimiteProdutos] = useState(300); // Valor padrão otimista até carregar

    // Estados do Formulário e Edição
    const [editingBanner, setEditingBanner] = useState(null);
    const [titulo, setTitulo] = useState('');
    const [linkType, setLinkType] = useState('url'); // 'url', 'categoria', 'produto'
    const [linkUrl, setLinkUrl] = useState('');
    const [selectedCategoriaId, setSelectedCategoriaId] = useState('');
    const [selectedProdutoId, setSelectedProdutoId] = useState('');
    const [file, setFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);

    // Estado para o Modal de Exclusão
    const [bannerToDelete, setBannerToDelete] = useState(null);

    // Paginação
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 6;

    const fetchData = async () => {
        try {
            // Busca dados do estabelecimento para validar o limite do plano atual
            const resEstab = await api.get(`/cardapio/estabelecimento/${estabelecimentoId}`).catch(() => ({ data: {} }));
            if (resEstab.data && resEstab.data.limite_produtos !== undefined) {
                setLimiteProdutos(Number(resEstab.data.limite_produtos));
            }

            const resBanners = await api.get(`/marketing/${estabelecimentoId}/banners`);
            setBanners(resBanners.data || []);
        } catch (err) {
            toast.error("Erro ao carregar banners");
        }
    };

    const fetchMenuData = async () => {
        try {
            const res = await api.get(`/cardapio/view/${estabelecimentoId}`);
            if (res.data) {
                setCategorias(res.data.categorias || []);
                setProdutos(res.data.itens || []);
            }
        } catch (err) {
            console.error("Erro ao carregar categorias e produtos", err);
        }
    };

    useEffect(() => {
        fetchData();
        fetchMenuData();
    }, [estabelecimentoId]);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            setPreviewUrl(URL.createObjectURL(selectedFile));
        }
    };

    const handleOpenCreate = () => {
        setEditingBanner(null);
        setTitulo('');
        setLinkType('url');
        setLinkUrl('');
        setFile(null);
        setPreviewUrl(null);
        setSelectedCategoriaId('');
        setSelectedProdutoId('');
        setIsModalOpen(true);
    };

    const handleOpenEdit = (banner) => {
        setEditingBanner(banner);
        setTitulo(banner.titulo || '');
        setFile(null);
        setPreviewUrl(banner.imagem_url ? (banner.imagem_url.startsWith('http') ? banner.imagem_url : `https://app.universomidiadigital.com.br${banner.imagem_url}`) : null);

        const url = banner.link_url || '';
        if (url.startsWith('#cat-')) {
            setLinkType('categoria');
            const catId = url.replace('#cat-', '');
            setSelectedCategoriaId(catId);
            setLinkUrl(url);
        } else if (url.includes('produto=')) {
            setLinkType('produto');
            const match = url.match(/produto=(\d+)/);
            if (match) setSelectedProdutoId(match[1]);
            setLinkUrl(url);
        } else {
            setLinkType('url');
            setLinkUrl(url);
            setSelectedCategoriaId('');
            setSelectedProdutoId('');
        }

        setIsModalOpen(true);
    };

    const handleLinkTypeChange = (type) => {
        setLinkType(type);
        if (type === 'url') {
            setLinkUrl('');
        } else if (type === 'categoria') {
            if (categorias.length > 0) {
                const firstId = categorias[0].id;
                setSelectedCategoriaId(firstId);
                setLinkUrl(`#cat-${firstId}`);
            }
        } else if (type === 'produto') {
            if (produtos.length > 0) {
                const firstId = produtos[0].id;
                setSelectedProdutoId(firstId);
                setLinkUrl(`?produto=${firstId}`);
            }
        }
    };

    const handleCategoryChange = (catId) => {
        setSelectedCategoriaId(catId);
        setLinkUrl(`#cat-${catId}`);
    };

    const handleProductChange = (prodId) => {
        setSelectedProdutoId(prodId);
        setLinkUrl(`?produto=${prodId}`);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!editingBanner && !file) return toast.error("Selecione uma imagem para o banner.");

        const formData = new FormData();
        formData.append('titulo', titulo);
        formData.append('link_url', linkUrl);
        if (file) {
            formData.append('imagem', file);
        }

        setLoading(true);
        try {
            if (editingBanner) {
                await api.put(`/marketing/banners/${editingBanner.id}`, formData);
                toast.success("Banner atualizado com sucesso!");
            } else {
                await api.post(`/marketing/${estabelecimentoId}/banners`, formData);
                toast.success("Banner criado com sucesso!");
            }
            setIsModalOpen(false);
            fetchData();
        } catch (err) {
            toast.error("Erro ao salvar banner");
        } finally {
            setLoading(false);
        }
    };

    const confirmDelete = async () => {
        if (!bannerToDelete) return;
        try {
            await api.delete(`/marketing/banners/${bannerToDelete}`);
            toast.success("Banner removido com sucesso!");
            fetchData();
        } catch (err) {
            toast.error("Erro ao remover banner");
        } finally {
            setBannerToDelete(null);
        }
    };

    // --- TRAVA DE PLANO (PLANO EXPRESS / ATÉ 50 PRODUTOS) ---
    if (limiteProdutos <= 50) {
        return (
            <div style={{ padding: '40px 20px', maxWidth: '700px', margin: '40px auto', fontFamily: 'inherit', textAlign: 'center' }}>
                <div style={{ background: '#fff', borderRadius: '24px', border: '1px solid #e2e8f0', padding: '40px', boxShadow: '0 10px 25px rgba(0,0,0,0.05)' }}>
                    <div style={{ background: '#fef2f2', width: '64px', height: '64px', borderRadius: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', color: '#ef4444' }}>
                        <Lock size={32} />
                    </div>
                    <h2 style={{ fontSize: '22px', fontWeight: '800', color: '#1e293b', marginBottom: '10px' }}>Recurso Indisponível no Plano Express</h2>
                    <p style={{ fontSize: '14px', color: '#64748b', lineHeight: '1.6', marginBottom: '25px' }}>
                        O gerenciamento de **Banners Promocionais** está disponível a partir do <b>Plano Standard</b> em diante. Faça o upgrade do seu plano para desbloquear este e outros recursos exclusivos.
                    </p>
                    <button 
                        onClick={() => {
                            // Redireciona para a aba de planos/upgrade dentro do painel
                            const abaUpgrade = document.querySelector('button[class*="nav-item"]'); // ou navegação interna
                            window.location.reload();
                        }}
                        style={{ background: corPrimaria, color: '#fff', border: 'none', padding: '12px 24px', borderRadius: '12px', fontWeight: 'bold', fontSize: '14px', cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                    >
                        Ver Planos e Fazer Upgrade
                    </button>
                </div>
            </div>
        );
    }

    // Paginação
    const totalPages = Math.ceil(banners.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentBanners = banners.slice(startIndex, startIndex + itemsPerPage);

    return (
        <div style={{ padding: '24px', maxWidth: '1100px', margin: '0 auto', fontFamily: 'inherit' }}>
            
            {/* Header da Seção */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '15px' }}>
                <div>
                    <h2 style={{ fontSize: '22px', fontWeight: '800', color: '#1e293b', margin: '0 0 4px 0' }}>Gerenciar Banners</h2>
                    <p style={{ fontSize: '14px', color: '#64748b', margin: 0 }}>Configure destaques visuais com links externos, categorias ou produtos.</p>
                </div>
                <button 
                    onClick={handleOpenCreate}
                    style={{ 
                        background: corPrimaria, 
                        color: '#fff', 
                        border: 'none', 
                        padding: '12px 20px', 
                        borderRadius: '16px', 
                        fontWeight: 'bold', 
                        cursor: 'pointer', 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        transition: 'transform 0.2s'
                    }}
                >
                    <Plus size={20} /> Novo Banner
                </button>
            </div>

            {/* Grid de Banners Estilo iFood */}
            {banners.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px 20px', background: '#fff', borderRadius: '24px', border: '1px solid #e2e8f0', color: '#94a3b8' }}>
                    <ImageIcon size={48} style={{ opacity: 0.4, marginBottom: '10px' }} />
                    <p style={{ fontSize: '15px', fontWeight: '600', margin: 0 }}>Nenhum banner cadastrado ainda.</p>
                </div>
            ) : (
                <>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px' }}>
                        {currentBanners.map(b => (
                            <div key={b.id} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '20px', overflow: 'hidden', boxShadow: '0 4px 15px rgba(0,0,0,0.03)', display: 'flex', flexDirection: 'column' }}>
                                <div style={{ position: 'relative', height: '140px', backgroundColor: '#f1f5f9' }}>
                                    <img 
                                        src={b.imagem_url.startsWith('http') ? b.imagem_url : `https://app.universomidiadigital.com.br${b.imagem_url}`} 
                                        alt={b.titulo} 
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                                    />
                                </div>
                                <div style={{ padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flex: 1 }}>
                                    <div style={{ overflow: 'hidden' }}>
                                        <h4 style={{ fontSize: '15px', fontWeight: '700', color: '#1e293b', margin: '0 0 4px 0', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                                            {b.titulo || 'Banner sem título'}
                                        </h4>
                                        <p style={{ fontSize: '12px', color: '#64748b', margin: 0, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                                            {b.link_url ? `Link: ${b.link_url}` : 'Sem redirecionamento'}
                                        </p>
                                    </div>
                                    <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                                        <button 
                                            onClick={() => handleOpenEdit(b)} 
                                            style={{ background: '#f1f5f9', border: 'none', color: '#334155', borderRadius: '12px', width: '38px', height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                                            title="Editar banner"
                                        >
                                            <Edit2 size={16} />
                                        </button>
                                        <button 
                                            onClick={() => setBannerToDelete(b.id)} 
                                            style={{ background: '#fee2e2', border: 'none', color: '#ef4444', borderRadius: '12px', width: '38px', height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                                            title="Excluir banner"
                                        >
                                            <Trash2 size={18} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Paginação */}
                    {totalPages > 1 && (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', marginTop: '30px' }}>
                            <button
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', opacity: currentPage === 1 ? 0.5 : 1 }}
                            >
                                <ChevronLeft size={20} color="#334155" />
                            </button>
                            
                            <span style={{ fontSize: '14px', fontWeight: '700', color: '#334155' }}>
                                Página {currentPage} de {totalPages}
                            </span>

                            <button
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                                style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', opacity: currentPage === totalPages ? 0.5 : 1 }}
                            >
                                <ChevronRight size={20} color="#334155" />
                            </button>
                        </div>
                    )}
                </>
            )}

            {/* MODAL DE CRIAÇÃO / EDIÇÃO DE BANNER */}
            {isModalOpen && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
                    <div style={{ background: '#fff', width: '100%', maxWidth: '500px', borderRadius: '24px', overflow: 'hidden', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)', display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}>
                        
                        <div style={{ padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#1e293b' }}>
                                {editingBanner ? 'Editar Banner' : 'Novo Banner'}
                            </h3>
                            <button onClick={() => setIsModalOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}>
                                <X size={20} />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} style={{ padding: '24px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            <div>
                                <label style={{ display: 'block', fontSize: '12px', fontWeight: '800', color: '#64748b', textTransform: 'uppercase', marginBottom: '8px' }}>Título do Banner (Opcional)</label>
                                <input 
                                    type="text" 
                                    value={titulo} 
                                    onChange={e => setTitulo(e.target.value)} 
                                    placeholder="Ex: Promoção de Fim de Semana" 
                                    style={{ width: '100%', padding: '12px 16px', borderRadius: '12px', border: '1.5px solid #e2e8f0', outline: 'none', fontSize: '14px', boxSizing: 'border-box' }} 
                                />
                            </div>

                            {/* Seletor do Tipo de Destino / Link */}
                            <div>
                                <label style={{ display: 'block', fontSize: '12px', fontWeight: '800', color: '#64748b', textTransform: 'uppercase', marginBottom: '8px' }}>Destino do Clique / Link</label>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '12px' }}>
                                    <button
                                        type="button"
                                        onClick={() => handleLinkTypeChange('url')}
                                        style={{ background: linkType === 'url' ? `${corPrimaria}15` : '#f8fafc', color: linkType === 'url' ? corPrimaria : '#64748b', border: `1.5px solid ${linkType === 'url' ? corPrimaria : '#e2e8f0'}`, padding: '10px 8px', borderRadius: '10px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                                    >
                                        <Link size={14} /> URL
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleLinkTypeChange('categoria')}
                                        style={{ background: linkType === 'categoria' ? `${corPrimaria}15` : '#f8fafc', color: linkType === 'categoria' ? corPrimaria : '#64748b', border: `1.5px solid ${linkType === 'categoria' ? corPrimaria : '#e2e8f0'}`, padding: '10px 8px', borderRadius: '10px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                                    >
                                        <Tag size={14} /> Categoria
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleLinkTypeChange('produto')}
                                        style={{ background: linkType === 'produto' ? `${corPrimaria}15` : '#f8fafc', color: linkType === 'produto' ? corPrimaria : '#64748b', border: `1.5px solid ${linkType === 'produto' ? corPrimaria : '#e2e8f0'}`, padding: '10px 8px', borderRadius: '10px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                                    >
                                        <Package size={14} /> Produto
                                    </button>
                                </div>

                                {linkType === 'url' && (
                                    <input 
                                        type="text" 
                                        value={linkUrl} 
                                        onChange={e => setLinkUrl(e.target.value)} 
                                        placeholder="https://..." 
                                        style={{ width: '100%', padding: '12px 16px', borderRadius: '12px', border: '1.5px solid #e2e8f0', outline: 'none', fontSize: '14px', boxSizing: 'border-box' }} 
                                    />
                                )}

                                {linkType === 'categoria' && (
                                    <select
                                        value={selectedCategoriaId}
                                        onChange={e => handleCategoryChange(e.target.value)}
                                        style={{ width: '100%', padding: '12px 16px', borderRadius: '12px', border: '1.5px solid #e2e8f0', outline: 'none', fontSize: '14px', background: '#fff', boxSizing: 'border-box' }}
                                    >
                                        {categorias.map(cat => (
                                            <option key={cat.id} value={cat.id}>{cat.nome}</option>
                                        ))}
                                    </select>
                                )}

                                {linkType === 'produto' && (
                                    <select
                                        value={selectedProdutoId}
                                        onChange={e => handleProductChange(e.target.value)}
                                        style={{ width: '100%', padding: '12px 16px', borderRadius: '12px', border: '1.5px solid #e2e8f0', outline: 'none', fontSize: '14px', background: '#fff', boxSizing: 'border-box' }}
                                    >
                                        {produtos.map(prod => (
                                            <option key={prod.id} value={prod.id}>{prod.nome} - R$ {parseFloat(prod.preco || 0).toFixed(2)}</option>
                                        ))}
                                    </select>
                                )}
                            </div>

                            <div>
                                <label style={{ display: 'block', fontSize: '12px', fontWeight: '800', color: '#64748b', textTransform: 'uppercase', marginBottom: '8px' }}>Imagem do Banner *</label>
                                <label style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: '2px dashed #cbd5e1', borderRadius: '12px', padding: '20px', cursor: 'pointer', backgroundColor: '#f8fafc', position: 'relative', overflow: 'hidden' }}>
                                    {previewUrl ? (
                                        <img src={previewUrl} alt="Preview" style={{ width: '100%', height: '120px', objectFit: 'cover', borderRadius: '8px' }} />
                                    ) : (
                                        <>
                                            <Upload size={28} color="#64748b" style={{ marginBottom: '8px' }} />
                                            <span style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>Clique para escolher a imagem</span>
                                            <span style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>PNG, JPG ou WEBP</span>
                                        </>
                                    )}
                                    <input type="file" onChange={handleFileChange} accept="image/*" style={{ display: 'none' }} />
                                </label>
                            </div>

                            <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                                <button 
                                    type="button" 
                                    onClick={() => setIsModalOpen(false)}
                                    style={{ flex: 1, background: '#f1f5f9', color: '#475569', border: 'none', padding: '12px', borderRadius: '12px', fontWeight: 'bold', cursor: 'pointer', fontSize: '15px' }}
                                >
                                    Cancelar
                                </button>
                                <button 
                                    type="submit" 
                                    disabled={loading} 
                                    style={{ flex: 1, background: corPrimaria, color: '#fff', border: 'none', padding: '12px', borderRadius: '12px', fontWeight: 'bold', cursor: 'pointer', fontSize: '15px', opacity: loading ? 0.7 : 1 }}
                                >
                                    {loading ? 'Salvando...' : (editingBanner ? 'Atualizar' : 'Adicionar')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* MODAL DE CONFIRMAÇÃO DE EXCLUSÃO */}
            {bannerToDelete && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
                    <div style={{ background: '#fff', width: '100%', maxWidth: '380px', borderRadius: '24px', padding: '24px', textAlign: 'center', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}>
                        <div style={{ background: '#fee2e2', width: '56px', height: '56px', borderRadius: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', color: '#ef4444' }}>
                            <Trash2 size={26} />
                        </div>
                        <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '800', color: '#1e293b' }}>Excluir Banner?</h3>
                        <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '24px' }}>Esta ação não poderá ser desfeita. Deseja realmente excluir este banner?</p>
                        
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button 
                                onClick={() => setBannerToDelete(null)}
                                style={{ flex: 1, background: '#f1f5f9', color: '#475569', border: 'none', padding: '12px', borderRadius: '12px', fontWeight: 'bold', cursor: 'pointer' }}
                            >
                                Cancelar
                            </button>
                            <button 
                                onClick={confirmDelete}
                                style={{ flex: 1, background: '#ef4444', color: '#fff', border: 'none', padding: '12px', borderRadius: '12px', fontWeight: 'bold', cursor: 'pointer' }}
                            >
                                Sim, Excluir
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}